import { useEffect, useState } from 'react';
import { AlertLayer } from '../components/Alert';
import type { CitizenSubmitResult } from '../api/contract';
import PcForm from './PcForm';
import PcSuccess from './PcSuccess';
import PcNotice from './PcNotice';
import { ASSOC_ADDRESS, ASSOC_COMPLAINT_TEL, ASSOC_NAME, ASSOC_TEL_HOURS, ASSOC_TELS } from '../util/assoc';
import { assetUrl } from '../util/asset-url';
import { usePageViewOnce } from '../util/page-view';

/**
 * PC 页外壳：抬头 → 正文 → 页脚。
 *
 * 这一页是**独立打开**的（协会官网挂链接进来），所以抬头/页脚必须自己带，
 * 不能指望外层页面提供。协会名称、咨询电话、办公地址一律照手机端「我的」页那几个
 * 真实常量（见 `../util/assoc.ts`），不自己编。
 *
 * ★ 页面上**没有手机壳、没有假状态栏、没有刘海**——那是手机端样张的物件。
 * ★ 弹窗层 `AlertLayer` 挂在最外层：它不在任何滚动容器里，
 *   所以无论用户滚到哪儿、页面有多长，提示都在同一个位置出现。
 *   （根因见 `components/Alert.tsx` 文件头：报错渲染 ≠ 报错可见。）
 */
export default function PcApp() {
  /* 访问人次埋点（2026-09-21）。这一路没有路由器，整个流程都在同一张页面里换面板，
     所以只在打开时报一次：**一次加载＝一人次**。见 util/page-view.ts。 */
  usePageViewOnce('/pc');
  const [done, setDone] = useState<CitizenSubmitResult | null>(null);
  /**
   * 投诉须知看过并同意了没有（2026-09-07 主公：「PC 端做一个投诉须知，同意后才显示表单」）。
   *
   * ★ 只存在内存里，**不落 localStorage**：须知是协会可随时修订的文本，
   *   记住上次的选择等于让人对着一份可能已经变了的文本继续「同意过」。
   *   理由详见 `PcNotice.tsx` 文件头。
   */
  const [agreed, setAgreed] = useState(false);

  // 拖文件到页面**空白处**时，浏览器默认会直接打开那个文件、把本页顶掉，
  // 表单里填了半天的东西就没了。整页兜住，只有落在上传区里才算数。
  useEffect(() => {
    const stop = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);

  // 提交成功后把视线带回页面顶部——结果面板比表单短得多，留在原滚动位置会看不见
  useEffect(() => {
    if (done) window.scrollTo({ top: 0 });
  }, [done]);

  return (
    <div className="pcpage">
      <header className="pchead">
        <div className="pchead-in">
          <a className="pcbrand" href="/pc.html">
            <img src={assetUrl("img/brand.png")} alt="" aria-hidden="true" />
            <span className="pcbrand-t">
              <span className="pcbrand-org">{ASSOC_NAME}</span>
              <span className="pcbrand-name">房诉通 · 在线投诉</span>
            </span>
          </a>
          {/* ★ 2026-09-08 主公定：这里露的是**投诉专线**，不是协会总咨询。
              在填投诉表单时卡住的人，要找的是管投诉的人 —— 给他总机等于再转一道。
              页脚仍列总咨询那两个号，两者并存不冲突（见 util/assoc.ts）。 */}
          <a className="pchead-tel" href={ASSOC_COMPLAINT_TEL.href}>
            <span className="pchead-tel-k">投诉咨询电话</span>
            <span className="pchead-tel-v">{ASSOC_COMPLAINT_TEL.display}</span>
          </a>
        </div>
      </header>

      <main className="pcmain">
        <div className="pcwrap">
          {/* 三态，顺序不能换：提交成功 → 结果页；没同意须知 → 须知；其余 → 表单。
              「再投一件」回到表单而不是回到须知：他刚刚才同意过，同一次访问里
              不必再问一遍；关掉页面重开则要重看（`agreed` 不落盘）。 */}
          {done ? (
            <PcSuccess result={done} onAgain={() => setDone(null)} />
          ) : agreed ? (
            <PcForm onDone={setDone} />
          ) : (
            <PcNotice onAgree={() => setAgreed(true)} />
          )}
        </div>
      </main>

      <footer className="pcfoot">
        <div className="pcfoot-in">
          <p className="pcfoot-org">{ASSOC_NAME}</p>
          <dl className="pcfoot-list">
            <div>
              <dt>咨询电话</dt>
              {/* ★ 两条线都要露出来：ASSOC_TEL_DISPLAY 是「只放得下一个」时的兼容出口，
                  页脚放得下就不该只放一个 —— 少露一个号等于让人少一条打得通的路。
                  页头那处位置确实窄，仍用单号。 */}
              <dd>
                {ASSOC_TELS.map((t, i) => (
                  <span key={t.display}>
                    {i > 0 ? <span className="pcfoot-sep"> / </span> : null}
                    <a href={t.href}>{t.display}</a>
                  </span>
                ))}
                <span className="pcfoot-hours">{ASSOC_TEL_HOURS}</span>
              </dd>
            </div>
            <div>
              <dt>办公地址</dt>
              <dd>{ASSOC_ADDRESS}</dd>
            </div>
          </dl>
        </div>
      </footer>

      <AlertLayer />
    </div>
  );
}
